import React, { useRef, useState } from "react";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { decodeAndSanitizeHtml } from "../textAnalysisUtils";
import {
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faSms,
  faChartBar,
  faSearch,
  faChevronUp,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { formatDateTime } from "../../../utils/DateTimeUtil";
import "../css/SmsAnalysisSimple.css";
import ThreatBannerShareButton from "../../common/ThreatBannerShareButton";
import OreInsightCard from "../../common/OreInsightCard";
import LinkAnalysisSection from "../../common/LinkAnalysisSection";
import SuggestionsBulletList from "../../common/SuggestionsBulletList";
import ReanalyzeWithContextPanel from "../../common/ReanalyzeWithContextPanel";
import AnalysisFeedbackBar from "../../common/AnalysisFeedbackBar";
import EmptyStatePanel from "../../common/EmptyStatePanel";

const SmsAnalysisDetailsSimple = ({
  selectedAnalysis,
  onScrollToAnalysisResults,
  showOriginalMessageInline,
  onToggleOriginalMessageInline,
  reportId,
  onReanalyzeWithContext,
  readOnly = false,
}) => {
  const reanalyzeRef = useRef(null);
  const { t } = useTranslation();

  const scrollToReanalyze = () => {
    if (reanalyzeRef.current) {
      reanalyzeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  if (!selectedAnalysis) {
    return (
      <>
        <div className="common-section-header">
          <h3>
            <FontAwesomeIcon icon={faChartBar} />
            {t("sms.section.analysisDetails")}
          </h3>
          <p>{t("sms.section.noSmsSelectedDesc")}</p>
        </div>
        <div className="panel-content details-panel-content">
          <EmptyStatePanel
            icon={faSms}
            title={t("sms.section.noSmsSelected")}
            description={t("sms.section.noSmsSelectedDesc")}
            className="no-analysis-selected"
          />
        </div>
      </>
    );
  }

  const formatDate = (date) => {
    if (!date) return "Unknown";
    return formatDateTime(date);
  };

  return (
    <>
      <div className="common-section-header">
        <h3>
          <FontAwesomeIcon icon={faChartBar} />
          {t("sms.section.analysisDetails")}
        </h3>
        <p>{t("sms.section.analysisDetailsSubtitle")}</p>
      </div>
      <div className="panel-content details-panel-content">
        <AnalysisSummarySimple
          selectedAnalysis={selectedAnalysis}
          formatDate={formatDate}
          reportId={reportId}
        />

        <AnalysisSuggestions selectedAnalysis={selectedAnalysis} />

        <LinkAnalysisSection
          urlAnalyses={selectedAnalysis?.analysis?.analysisResult?.urlAnalyses}
        />

        <AnalysisReasoning selectedAnalysis={selectedAnalysis} />

        {!readOnly && (
          <AnalysisFeedbackBar
            analysisKey={selectedAnalysis?.id || selectedAnalysis?.key}
            onNegativeFeedback={scrollToReanalyze}
          />
        )}

        {!readOnly && (
          <div ref={reanalyzeRef}>
            <ReanalyzeWithContextPanel
              initialContext={selectedAnalysis?.userContext || ""}
              onSubmit={(context) =>
                onReanalyzeWithContext?.(selectedAnalysis, context)
              }
            />
          </div>
        )}

        <OreInsightCard
          oreData={selectedAnalysis.analysis?.analysisResult}
          channel="sms"
        />

        <MessageContent
          selectedAnalysis={selectedAnalysis}
          showOriginalMessageInline={showOriginalMessageInline}
          onToggleOriginalMessageInline={onToggleOriginalMessageInline}
        />
      </div>
    </>
  );
};

const AnalysisSummarySimple = ({ selectedAnalysis, formatDate, reportId }) => {
  const analysisResult = selectedAnalysis?.analysis?.analysisResult || {};
  const { isScam, confidence } = analysisResult;
  const { t } = useTranslation();

  // Helper function to get threat banner class
  const getThreatBannerClass = () => {
    if (isScam) {
      return "threat-detected";
    } else if (confidence <= 0.2) {
      return "security-failed";
    } else {
      return "safe";
    }
  };

  // Helper function to get status icon
  const getStatusIcon = () => {
    return isScam ? faExclamationTriangle : faCheckCircle;
  };

  return (
    <div className="case-details">
      {/* Threat Assessment Banner */}
      <div className={`threat-banner ${getThreatBannerClass()}`}>
        <div className="threat-icon">
          <FontAwesomeIcon icon={getStatusIcon()} />
        </div>
        <div className="threat-content">
          <h2 className="threat-title">
            {isScam ? t("sms.detail.scamDetected") : t("sms.detail.noThreats")}
          </h2>
          <p className="threat-subtitle">
            {isScam
              ? t("sms.detail.scamSubtitle")
              : t("sms.detail.safeSubtitle")}
          </p>
        </div>
        <ThreatBannerShareButton
          scamDetected={isScam === true}
          threatType={t("sms.share.threatType")}
          threatTitle={t("sms.share.threatTitle")}
          threatDetail={t("sms.detail.scamSubtitle")}
          reportId={reportId}
        />
      </div>
    </div>
  );
};

const AnalysisSuggestions = ({ selectedAnalysis }) => {
  const suggestions = selectedAnalysis?.analysis?.analysisResult?.suggestions;
  const { t } = useTranslation();

  if (!suggestions) {
    return null;
  }

  return (
    <div className="suggestions-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="fa-icon-margin-right-8"
        />
        {t("sms.section.whatToDo")}
      </h4>
      <SuggestionsBulletList text={suggestions} />
    </div>
  );
};

const AnalysisReasoning = ({ selectedAnalysis }) => {
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const reasoning = selectedAnalysis?.analysis?.analysisResult?.reasoning;
  const { t } = useTranslation();

  if (!reasoning) {
    return null;
  }

  return (
    <div className="explanation-section">
      <h4
        className="subsection-title collapsible-header"
        onClick={() => setExplanationCollapsed(!explanationCollapsed)}
        style={{ cursor: "pointer" }}
      >
        <FontAwesomeIcon icon={faSearch} className="fa-icon-margin-right-8" />
        {t("sms.section.explanation")}
        <FontAwesomeIcon
          icon={explanationCollapsed ? faChevronDown : faChevronUp}
          style={{ marginLeft: "auto", fontSize: "0.85rem" }}
        />
      </h4>
      {!explanationCollapsed && (
        <div
          className="explanation-text"
          dangerouslySetInnerHTML={{
            __html: decodeAndSanitizeHtml(reasoning || ""),
          }}
        />
      )}
    </div>
  );
};

const MessageContent = ({
  selectedAnalysis,
  showOriginalMessageInline,
  onToggleOriginalMessageInline,
}) => {
  const messageContent = selectedAnalysis?.messageContent;
  const { t } = useTranslation();

  const handleShowOriginalMessage = () => {
    if (onToggleOriginalMessageInline) {
      const newShowState = !showOriginalMessageInline;
      onToggleOriginalMessageInline(newShowState);

      if (newShowState) {
        setTimeout(() => {
          const messageContentElement = document.querySelector(
            ".original-message-content",
          );
          if (messageContentElement) {
            messageContentElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
        }, 100);
      }
    }
  };

  return (
    <>
      <div className="common-form-group">
        <h4>
          <FontAwesomeIcon icon={faSms} className="fa-icon-margin-right-8" />
          {t("sms.message.viewOriginal")}
        </h4>
        <div className="common-button-group">
          <button
            className="common-button show-email-button"
            onClick={handleShowOriginalMessage}
          >
            <FontAwesomeIcon icon={faSms} className="fa-icon-margin-right-8" />
            {showOriginalMessageInline
              ? t("sms.message.hide")
              : t("sms.message.show")}
          </button>
        </div>
      </div>

      {showOriginalMessageInline && (
        <div className="analysis-explanation original-message-content">
          {messageContent ? (
            <div
              className="message-full-content"
              style={{
                padding: "1rem",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                border: "1px solid var(--border-color)",
              }}
            >
              {messageContent}
            </div>
          ) : (
            <div className="message-full-content">
              <p>
                <strong>{t("sms.message.noContent")}</strong>
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SmsAnalysisDetailsSimple;
